import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { TripReviewCard as TripReviewCardDesignSystem } from '../../../design-system/components/card/tripReview/TripReviewCard';
import { ReviewRatingProps } from '../../../design-system/components/review/rating/ReviewRating';
import { TripReview } from '../../../resources/tripReview/domain/types/tripReview';
import { convertDateToUTC } from '../../utilities/date';
import { PickDomain } from '../../utilities/types/PickDomain';
import { ReviewRatingFallback } from '../ReviewRating/ReviewRatingFallback';

export interface Props {
    tripReview: PickDomain<
        TripReview,
        'creationDate' | 'rating',
        {
            travelerName: TripReview['travelerName'] | null;
            tripDescription: TripReview['tripDescription'] | null;
        }
    >;
    onClickTripReview: () => void;
    trackingInformation?: {
        clickTripReview: TrackingProperties<typeof ampli.tripReviewClicked>;
        clickAgency: TrackingProperties<typeof ampli.agencyClicked> | null;
    };
}

export function TripReviewCard({ tripReview, onClickTripReview, trackingInformation }: Props) {
    const { formatDate, formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const reviewRatingProps: ReviewRatingProps = {
        ariaLabel: (rating) =>
            formatMessage(
                { id: 'global.component.reviewRating.iconsDescription' },
                {
                    rating: rating.value,
                    maxRating: rating.max,
                },
            ),
        ratingValue: tripReview.rating.value,
        maxRating: tripReview.rating.max,
        minRating: tripReview.rating.min,
        fallback: (error) => <ReviewRatingFallback error={error} />,
    };

    const handleTripReviewClicked = useCallback(() => {
        if (typeof onClickTripReview === 'function') {
            onClickTripReview();
        }

        if (trackingInformation?.clickTripReview) {
            trackEvent((ampli, defaultProperties) => {
                ampli.tripReviewClicked({
                    ...defaultProperties,
                    ...trackingInformation.clickTripReview,
                });
            });
        }
    }, [onClickTripReview, trackEvent, trackingInformation?.clickTripReview]);

    return (
        <TripReviewCardDesignSystem
            travelerName={tripReview.travelerName}
            creationDate={tripReview.creationDate}
            creationDateFormatted={formatDate(convertDateToUTC(new Date(tripReview.creationDate)), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            })}
            tripDescription={tripReview.tripDescription}
            seeMoreLabel={formatMessage({ id: 'global.component.tripReviewCard.seeMore' })}
            onClickTripReview={handleTripReviewClicked}
            reviewRatingProps={reviewRatingProps}
        />
    );
}
